import { useEffect, useState } from 'react';
import { CopyIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useAppSelector } from '../../../redux/hooks';
import { getAppAnalytics } from '../../../redux/viewApp/appAnalytics/selectors';
import {
  fetchAppLogsFromPastDay,
  fetchSessionLogs,
} from '../../../utils/app-analytics/fetch-logs';

// Define a type for the log entries
interface LogEntry {
  sessionId: string;
  date: string;
  deviceId: string;
  deviceType: string;
  providerId: string;
  applicationId: string;
  publicIpAddress: string;
  logType: string;
  appName: string;
  providerName: string;
}

interface LiveFeedLog {
  sessionId: string;
  latest_timestamp: string;
  deviceType: string;
  deviceId: string;
  logType: string;
}

export const LiveFeedMetrics = () => {
  const appAnalytics = useAppSelector(getAppAnalytics);
  const [appLogs, setAppLogs] = useState<LiveFeedLog[]>([]);
  const [selectedSessionLogs, setSelectedSessionLogs] = useState<LogEntry[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  useEffect(() => {
    const fetchData = async () => {
      if (appLogs?.length === 0) {
        const appLogData = await fetchAppLogsFromPastDay(appAnalytics.appDetail.appId);
        setAppLogs(appLogData);
      }
    };

    fetchData();
  }, []);

  const getStatusThemeFromLogType = (logType: string) => {
    if (logType === 'PROOF_GENERATION_SUCCESS' || logType === 'PROOF_SUBMITTED') {
      return 'green';
    } else if (logType === 'PROOF_GENERATION_FAILED') {
      return 'red';
    } else {
      return 'blue';
    }
  };

  const getStatusFontColorFromLogType = (logType: string) => {
    if (logType === 'PROOF_GENERATION_SUCCESS' || logType === 'PROOF_SUBMITTED') {
      return 'green';
    } else if (logType === 'PROOF_GENERATION_FAILED') {
      return 'red';
    } else {
      return 'blue.400';
    }
  };

  const getStatusFromLogType = (logType: string) => {
    if (logType === 'PROOF_GENERATION_SUCCESS' || logType === 'PROOF_SUBMITTED') {
      return 'Completed';
    } else if (logType === 'PROOF_GENERATION_FAILED') {
      return 'Failed';
    } else {
      return 'Started';
    }
  };

  const getSessionLogs = async (sessionId: string) => {
    const data = await fetchSessionLogs(sessionId);
    setSelectedSessionLogs(data);
    setSelectedSessionId(sessionId);
    onOpen();
  };

  const cellStyle = {
    padding: '4px 12px',
    textAlign: 'left' as const,
    whiteSpace: 'wrap' as const,
    maxWidth: '0',
    overflow: 'hidden',
  };

  const truncateSessionId = (sessionId: string) => {
    const parts = sessionId.split('-');
    return `...${parts[parts.length - 1]}`;
  };

  const copySessionId = () => {
    navigator.clipboard.writeText(selectedSessionId);
    toast({
      title: 'Session ID copied',
      status: 'success',
      duration: 2000,
      isClosable: true,
      position: 'top-right',
    });
  };

  return (
    <Box
      borderRadius="16px"
      mt="30px"
      w="50%"
      h="26vh"
      bgColor="#F7F9FB"
      borderWidth="1px"
      boxShadow="2px"
    >
      <VStack align="flex-start" p={5} w="100%" h="100%" spacing={6}>
        <VStack w="100%" align="flex-start" h="10%">
          <Text
            color="black"
            className="small-font"
            whiteSpace="pre-line"
            style={{ fontWeight: 650 }}
            fontWeight="bold"
            fontFamily="inter"
          >
            Live Feed of Past 24 Hours
          </Text>
        </VStack>
        <VStack w="100%" h="80%">
          <table
            style={{
              width: '100%',
              borderCollapse: 'separate',
              borderSpacing: '0 1px',
            }}
          >
            <thead>
              <tr style={{ position: 'relative' }}>
                <th style={cellStyle}>
                  <Text
                    fontFamily="inter"
                    color="#00000066"
                    fontWeight="normal"
                    className="x-small-font"
                  >
                    Session Id
                  </Text>
                </th>
                <th style={cellStyle}>
                  <Text
                    fontFamily="inter"
                    color="#00000066"
                    fontWeight="normal"
                    className="x-small-font"
                  >
                    Date (GMT)
                  </Text>
                </th>
                <th style={cellStyle}>
                  <Text
                    fontFamily="inter"
                    color="#00000066"
                    fontWeight="normal"
                    className="x-small-font"
                  >
                    Device
                  </Text>
                </th>
                <th style={cellStyle}>
                  <Text
                    fontFamily="inter"
                    color="#00000066"
                    fontWeight="normal"
                    className="x-small-font"
                  >
                    Status
                  </Text>
                </th>
              </tr>
              <tr
                style={{
                  height: '4px',
                  position: 'absolute',
                  bottom: '-4px',
                  left: 0,
                  right: 0,
                }}
              >
                <td colSpan={4} style={{ borderBottom: '1px solid #E2E8F0' }} />
              </tr>
            </thead>
          </table>
          <Box
            width="100%"
            borderTop="1px"
            borderColor="lightgray"
            h="100%"
            overflowY="auto"
            overflowX="auto"
            css={{
              '&::-webkit-scrollbar': {
                width: '8px',
                height: '8px',
              },
              '&::-webkit-scrollbar-track': {
                width: '6px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#CBD5E0',
                borderRadius: '24px',
              },
            }}
          >
            {appLogs?.length ? (
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'separate',
                  borderSpacing: '0 8px',
                }}
              >
                <tbody>
                  {appLogs.map((log: LiveFeedLog, i: number) => (
                    <tr
                      key={i}
                      style={{ marginBottom: '8px', cursor: 'pointer' }}
                      onClick={() => getSessionLogs(log.sessionId)}
                    >
                      <td style={cellStyle}>
                        <Text
                          className="xx-small-font"
                          color="#000"
                          fontWeight={350}
                          fontFamily="inter"
                        >
                          {truncateSessionId(log.sessionId)}
                        </Text>
                      </td>
                      <td style={cellStyle}>
                        <Text
                          className="xx-small-font"
                          color="#000"
                          fontWeight={350}
                          fontFamily="inter"
                        >
                          {log.latest_timestamp}
                        </Text>
                      </td>
                      <td style={cellStyle}>
                        <Text
                          className="xx-small-font"
                          color="#000"
                          fontWeight={350}
                          fontFamily="inter"
                        >
                          {log.deviceType}
                        </Text>
                      </td>
                      <td style={cellStyle}>
                        <Tag
                          size="sm"
                          colorScheme={getStatusThemeFromLogType(log.logType)}
                        >
                          <Text
                            className="xx-small-font"
                            color={getStatusFontColorFromLogType(log.logType)}
                            fontWeight={350}
                            fontFamily="inter"
                          >
                            {getStatusFromLogType(log.logType)}
                          </Text>
                        </Tag>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <VStack w="100%" mt={8} justifyContent="center">
                <Text fontFamily="inter" className="small-font" color="gray.400">
                  No logs available
                </Text>
              </VStack>
            )}
          </Box>
        </VStack>
      </VStack>

      <Modal isOpen={isOpen} onClose={onClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Text
              fontFamily="inter"
              className="small-font"
              style={{ fontWeight: 650 }}
              fontWeight="bold"
            >
              Session Logs
            </Text>
            <VStack align="flex-start" mt={4} spacing={4}>
              <HStack w="100%">
                <Text fontFamily="inter" className="x-small-font" mr={2} w="15%">
                  Session ID:
                </Text>
                <Text fontFamily="inter" className="x-small-font" color="gray">
                  {selectedSessionId}
                </Text>
                <IconButton
                  aria-label="Copy session ID"
                  icon={<CopyIcon fontSize="12px" />}
                  size="xs"
                  ml={2}
                  onClick={copySessionId}
                />
              </HStack>
              <HStack w="100%">
                <Text fontFamily="inter" w="15%" className="x-small-font" mr={2}>
                  Provider ID:
                </Text>
                <Text color="gray" fontFamily="inter" className="x-small-font">
                  {selectedSessionLogs[0]?.providerId || 'N/A'}
                </Text>
              </HStack>
              <HStack w="100%">
                <Text fontFamily="inter" w="15%" className="x-small-font" mr={2}>
                  Device Type:
                </Text>
                <Text color="gray" fontFamily="inter" className="x-small-font">
                  {selectedSessionLogs[0]?.deviceType !== 'NA'
                    ? selectedSessionLogs[0]?.deviceType
                    : 'N/A'}
                </Text>
              </HStack>
              <HStack w="100%">
                <Text fontFamily="inter" className="x-small-font" w="15%" mr={2}>
                  Device ID:
                </Text>
                <Text fontFamily="inter" color="gray" className="x-small-font">
                  {selectedSessionLogs[0]?.deviceId !== 'NA'
                    ? selectedSessionLogs[0]?.deviceId
                    : 'N/A'}
                </Text>
              </HStack>
            </VStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <TableContainer>
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>
                      <Text
                        fontFamily="inter"
                        className="x-small-font"
                        style={{ fontWeight: 650 }}
                        fontWeight="bold"
                      >
                        Timestamp
                      </Text>
                    </Th>
                    <Th>
                      <Text
                        fontFamily="inter"
                        style={{ fontWeight: 650 }}
                        className="x-small-font"
                        fontWeight="bold"
                      >
                        Log Type
                      </Text>
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {selectedSessionLogs.map((log, index) => (
                    <Tr key={index}>
                      <Td>
                        <Text fontFamily="inter" className="x-small-font">
                          {log.date}
                        </Text>
                      </Td>
                      <Td>
                        <Tag
                          size="sm"
                          colorScheme={getStatusThemeFromLogType(log.logType)}
                          borderRadius="full"
                        >
                          <Text
                            fontFamily="inter"
                            color="blue.600"
                            className="xx-small-font"
                          >
                            {log.logType}
                          </Text>
                        </Tag>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" className="x-small-font" mr={3} onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};
